// Профиль путешественника: имя и контакт для заявок, гражданство, срок паспорта, город «откуда я обычно еду»,
// попутчики (семья, друзья), валюта, прямые рейсы. Хранится в браузере; подставляется в поиск, брони, тур и расходы.
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { placeItem, cleanName } from '../../entities/place';
import { api } from '../../shared/lib/api';
import { bus } from '../../shared/lib/bus';
import { openMyBookings } from '../../shared/lib/booking';
import { getLang } from '../../shared/i18n/i18n';
import { store } from '../../shared/lib/storage';
import { createStore, useStore } from '../../shared/lib/store';
import type { HomeCity, Profile, Traveler } from '../../shared/lib/types';
import { Autocomplete, type Picked } from '../../shared/ui/Autocomplete';
import { Dialog } from '../../shared/ui/Dialog';
import { Icon } from '../../shared/ui/Icon';
import { Select } from '../../shared/ui/Select';
import { toast } from '../../shared/ui/Toast';
import { passportOptions } from '../destination/model';
import { passportStore, setPassport } from '../destination/passport';
import { cleanTravelers, partyOf, passportProblem } from './model';
import './profile.css';

export { partyOf, travelerNames } from './model';

const KEY = 'profile';

export const getProfile = (): Profile => {
  const p = store.get<Profile>(KEY, {});
  return p && typeof p === 'object' ? { ...p, travelers: cleanTravelers(p.travelers) } : {};
};

/** Профиль в компоненте: обновляется, когда его сохранили */
export function useProfile(): Profile {
  const [p, setP] = useState<Profile>(getProfile);
  useEffect(() => bus.on('profile:changed', () => setP(getProfile())), []);
  return p;
}

/** Сохранить профиль целиком и сообщить разделам */
export function saveProfile(p: Profile): void {
  store.set(KEY, p);
  bus.emit('profile:changed', p);
}

/** Имя и контакт из отправленной заявки — в профиль, если там их ещё нет (в следующий раз не вводить заново) */
export function rememberContact(name: string, contact: string): void {
  const p = getProfile();
  const patch: Profile = {};
  if (!p.name && name.trim()) patch.name = name.trim().slice(0, 60);
  if (!p.contact && contact.trim()) patch.contact = contact.trim().slice(0, 80);
  if (Object.keys(patch).length) saveProfile({ ...p, ...patch });
}

/** Сколько человек едет по профилю: сам путешественник и попутчики */
export const profileParty = () => partyOf(getProfile());

export const CURRENCIES = [
  { value: 'usd', label: 'USD' }, { value: 'rub', label: 'RUB' }, { value: 'eur', label: 'EUR' }, { value: 'uzs', label: 'UZS' }, { value: 'kzt', label: 'KZT' },
];

/**
 * Предупреждение о паспорте для поездки, которая заканчивается в lastDay (YYYY-MM-DD): свой паспорт и паспорта попутчиков.
 * Многие страны требуют, чтобы паспорт действовал ещё минимум 6 месяцев после въезда или выезда.
 */
export function passportWarning(lastDay: string | null | undefined): string {
  const p = getProfile();
  if (!lastDay) return '';
  const own = passportProblem(p.passport, lastDay);
  const own2 = own === 'expired' ? `Паспорт истекает ${longDate(p.passport!)} — раньше конца поездки. Его нужно заменить до вылета.`
    : own === 'short' ? `Паспорт действует до ${longDate(p.passport!)}: меньше 6 месяцев после поездки. Многие страны требуют запас в 6 месяцев — проверьте правила страны.`
      : '';
  const others = (p.travelers || []).filter((t) => passportProblem(t.passport, lastDay)).map((t) => `${t.name} — до ${longDate(t.passport!)}`);
  return [own2, others.length ? `Паспорт попутчиков меньше чем на 6 месяцев после поездки: ${others.join(', ')}.` : ''].filter(Boolean).join(' ');
}

const longDate = (iso: string): string => new Date(`${iso}T12:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

const profileUi = createStore({ open: false });
export const openProfile = (): void => profileUi.set({ open: true });

/** ДД.ММ.ГГГГ ↔ YYYY-MM-DD */
const toRu = (iso: string | undefined): string => (iso ? iso.split('-').reverse().join('.') : '');
function fromRu(raw: string): string | null {
  const v = raw.trim();
  if (!v) return '';
  const m = v.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!m || Number(m[2]) > 12 || Number(m[2]) < 1 || Number(m[1]) > 31 || Number(m[1]) < 1) return null;
  const iso = `${m[3]}-${m[2]}-${m[1]}`;
  return Number.isNaN(Date.parse(iso)) ? null : iso;
}

// Маска ДД.ММ.ГГГГ
const mask = (e: FormEvent<HTMLInputElement>) => {
  const el = e.currentTarget;
  const d = el.value.replace(/\D/g, '').slice(0, 8);
  el.value = [d.slice(0, 2), d.slice(2, 4), d.slice(4)].filter(Boolean).join('.');
};

/** Попутчик в форме: срок паспорта — как ввели (ДД.ММ.ГГГГ) */
type DraftTraveler = Omit<Traveler, 'passport'> & { passport: string };

const AGES = [{ value: '', label: 'Взрослый' }, ...Array.from({ length: 18 }, (_, i) => ({ value: String(i), label: i === 0 ? 'Ребёнок до 1 года' : `Ребёнок, ${i} ${i === 1 ? 'год' : i < 5 ? 'года' : 'лет'}` }))];

export function ProfileDialog() {
  const { open } = useStore(profileUi);
  const name = useRef<HTMLInputElement>(null);
  const contact = useRef<HTMLInputElement>(null);
  const passport = useRef<HTMLInputElement>(null);
  const direct = useRef<HTMLInputElement>(null);
  const [currency, setCurrency] = useState('usd');
  const [citizenship, setCitizenship] = useState('UZ');
  const [home, setHome] = useState<Picked | null>(null);
  const [mates, setMates] = useState<DraftTraveler[]>([]);
  const [codes, setCodes] = useState<string[]>([]);
  const [result, setResult] = useState('');

  // Открытие: заполняем поля сохранённым профилем
  useEffect(() => {
    if (!open) return;
    const p = getProfile();
    setCurrency(p.currency || store.get('currency', 'usd'));
    setCitizenship(passportStore.get().passport);
    setHome(p.home ? { label: `${p.home.name} (${p.home.code})`, value: p.home.code } : null);
    setMates((p.travelers || []).map((t) => ({ ...t, passport: toRu(t.passport) })));
    setResult('');
    if (name.current) name.current.value = p.name || '';
    if (contact.current) contact.current.value = p.contact || '';
    if (passport.current) passport.current.value = toRu(p.passport);
    if (direct.current) direct.current.checked = Boolean(p.direct);
    // Список гражданств (~200 стран) — один раз
    if (!codes.length) api<{ passports?: string[] }>('/api/visa/passports').then((x) => setCodes(x.passports || [])).catch(() => {});
  }, [open]);

  const countries = useMemo(() => passportOptions(codes, getLang()), [codes]);

  const setMate = (i: number, patch: Partial<DraftTraveler>) => setMates((list) => list.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const addMate = () => setMates((list) => (list.length >= 9 ? list : [...list, { id: `t${Date.now().toString(36)}${list.length}`, name: '', age: null, citizenship: citizenship, passport: '' }]));

  const save = (e: FormEvent) => {
    e.preventDefault();
    const iso = fromRu(passport.current?.value || '');
    if (iso === null) { setResult('Срок паспорта — в формате ДД.ММ.ГГГГ'); return; }
    const travelers: Traveler[] = [];
    for (const m of mates) {
      if (!m.name.trim()) continue;
      const exp = fromRu(m.passport);
      if (exp === null) { setResult(`Срок паспорта попутчика ${m.name.trim()} — в формате ДД.ММ.ГГГГ`); return; }
      travelers.push({ id: m.id, name: m.name.trim().slice(0, 60), age: m.age, ...(m.citizenship ? { citizenship: m.citizenship } : {}), ...(exp ? { passport: exp } : {}) });
    }
    const homeCity: HomeCity | null = home?.value ? { code: home.value, name: cleanName(home.label) } : null;
    const p: Profile = {
      name: (name.current?.value || '').trim(),
      contact: (contact.current?.value || '').trim(),
      passport: iso,
      currency,
      direct: Boolean(direct.current?.checked),
      home: homeCity,
      travelers: cleanTravelers(travelers),
    };
    setPassport(citizenship);
    saveProfile(p);
    profileUi.set({ open: false });
    const warn = passportWarning(new Date().toISOString().slice(0, 10));
    toast(warn || 'Профиль сохранён');
  };

  return (
    <Dialog id="profile-dialog" open={open} onClose={() => profileUi.set({ open: false })} className="profile-dialog">
      <div className="dialog-icon"><Icon name="id" /></div>
      <h2>Профиль путешественника</h2>
      <p className="muted">Хранится в этом браузере. Подставляем в поиск, заявки, брони и тур, предупреждаем о сроке паспорта.</p>
      <form id="profile-form" className="stack" noValidate onSubmit={save}>
        <label className="field-light">Имя<input id="pf-name" ref={name} maxLength={60} autoComplete="name" /></label>
        <label className="field-light">Телефон или Telegram<input id="pf-contact" ref={contact} maxLength={80} autoComplete="tel" placeholder="+998 90 123-45-67 или @username" /></label>
        <div className="pf-row">
          <label className="field-light">Гражданство
            <Select id="pf-citizenship" value={citizenship} options={countries.length ? countries : [{ value: citizenship, label: citizenship }]} onChange={setCitizenship} />
          </label>
          <label className="field-light">Паспорт действует до<input id="pf-passport" ref={passport} maxLength={10} inputMode="numeric" placeholder="ДД.ММ.ГГГГ" onInput={mask} /></label>
        </div>
        <div className="pf-home">
          <Autocomplete id="pf-home" label="Откуда я обычно еду" icon="home" placeholder="Город или аэропорт" endpoint="/api/places" mapItem={placeItem}
            value={home} onChange={setHome} />
          <p className="muted small">Город подставится в «Откуда» у билетов и поездов.</p>
        </div>

        <fieldset className="pf-mates">
          <legend>Попутчики</legend>
          <p className="muted small">Семья и друзья, с кем вы обычно едете: число пассажиров, имена в расходах, виза для каждого.</p>
          {mates.map((m, i) => (
            <div className="pf-mate" key={m.id}>
              <input className="pf-mate-name" aria-label="Имя попутчика" placeholder="Имя" maxLength={60} value={m.name} onChange={(e) => setMate(i, { name: e.target.value })} />
              <Select compact ariaLabel="Возраст" value={m.age === null ? '' : String(m.age)} options={AGES} onChange={(v) => setMate(i, { age: v === '' ? null : Number(v) })} />
              <Select compact ariaLabel="Гражданство попутчика" value={m.citizenship || citizenship} options={countries.length ? countries : [{ value: m.citizenship || citizenship, label: m.citizenship || citizenship }]}
                onChange={(v) => setMate(i, { citizenship: v })} />
              <input className="pf-mate-passport" aria-label="Паспорт попутчика действует до" placeholder="Паспорт до" maxLength={10} inputMode="numeric"
                value={m.passport} onInput={mask} onChange={(e) => setMate(i, { passport: e.target.value })} />
              <button type="button" className="icon-btn" title="Убрать" aria-label={`Убрать ${m.name || 'попутчика'}`} onClick={() => setMates((list) => list.filter((_, j) => j !== i))}><Icon name="x" /></button>
            </div>
          ))}
          {mates.length < 9 && <button type="button" className="ghost small" id="pf-add-mate" onClick={addMate}><Icon name="plus" />Добавить попутчика</button>}
        </fieldset>

        <label className="field-light">Валюта цен
          <Select id="pf-currency" value={currency} options={CURRENCIES} onChange={setCurrency} />
        </label>
        <label className="switch small"><input id="pf-direct" ref={direct} type="checkbox" /><i /> Предпочитаю прямые рейсы</label>
        <button type="submit" className="btn block">Сохранить</button>
        <button type="button" className="ghost block" id="pf-bookings" onClick={() => { profileUi.set({ open: false }); openMyBookings(); }}><Icon name="calendar-plus" />Мои брони</button>
        <p id="profile-result" className="muted">{result}</p>
      </form>
    </Dialog>
  );
}
